"use strict";
import { CircularProgress, Grid, Skeleton, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import SubtitlesOutlinedIcon from "@mui/icons-material/SubtitlesOutlined";
import React, {
    useEffect,
    useState,
    useCallback,
    useRef,
    useContext,
    useMemo,
} from "react";
import classes from "../../Assets/Styles/QAScreen.module.css";
// Shared dialogue table styles, so QA matches the performance direction page.
import dialogueClasses from "../../Assets/Styles/VoiceQAScreen.module.css";
import Loader from "../../Components/Loader";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Store } from "../../Store";
import {
  GET_ALL_QUALITYASSURANCE_NOTES,
  GET_ALL_QUALITYASSURANCE_VERSIONS,
  GET_CHARACTERS_FOR_PROJECT,
  GET_SPEECH_FOR_PROJECT_BY_PAGE,
  GET_SPEECH_POSITION_AROUND_TIME,
  SEARCH_SPEECH_FOR_PROJECT,
  UPDATE_ONE_QUALITYASSURANCE_NOTE,
  UPDATE_ONE_SPEECH,
  SRT,
  GET_SRT
} from "../../ApiEndpoints";
import {
  convertToDate,
  formatTime,
  timeStringToSeconds,
} from "../../utils/generalFunctions";
import {
  ensureProjectFinalLanguageTranslations,
  findTranslationByLanguageCode,
  getDefaultVoiceIdForEmotion,
  getSpeakerDisplayName,
  getTranslationDurationMessage,
} from "../../features/projectWork";
import { companyName } from "../../utils/constants";
import { backgroundWellSx } from "../../theme";
import MediaDownloadMenu from "../../Components/MediaDownloadMenu";
import { buildMediaDownloadItems } from "../../utils/versionMedia";
import TaskButton from "../../Components/Buttons/TaskButton";
import { ToastContainer, toast } from "react-toastify";
import ClipAudioPlayer from "../../Components/Players/ClipAudioPlayer";
import NativeClipAudioPlayer from "../../Components/Players/NativeClipAudioPlayer";
import UnifiedVideoPlayer from "../../Components/Players/UnifiedVideoPlayer";
import VolumeSelector from "../../Components/AudioTools/VolumeSelector";
import { hydrateDialoguePlaybackUrls } from "../../utils/audioMedia";
import {
  audioLayoutLabel,
  isPlayableVersion,
  PLAYBACK,
  versionPlayback,
  videoSourceLabel,
} from "../../features/exportLayout/audioLayout";
import DownloadOnlyNotice from "../../features/exportLayout/DownloadOnlyNotice";
import { useDialoguePlaybackRefresh } from "../../hooks/useDialoguePlaybackRefresh";
import { useProjectMediaPlayback } from "../../hooks/useProjectMediaPlayback";
import {
  DEFAULT_DIALOGUE_SEARCH_FILTERS,
  hasAnyDialogueSearchFilter,
} from "../../Components/DialogueSearchFilter/DialogueSearchFilter";
import DialogueFilterBar, {
  DIALOGUE_ATTENTION_OPTIONS,
} from "../../Components/DialogueSearchFilter/DialogueFilterBar";


// Varied line widths so placeholder rows read like real dialogue text.
const SKELETON_LINE_WIDTHS = [
  ["92%", "78%", "55%"],
  ["85%", "64%"],
  ["96%", "88%", "71%"],
  ["74%", "58%"],
  ["90%", "82%", "40%"],
];

// Skeleton rows shown in place of the dialogues while a page loads.
const DialogueTableSkeletonRows = ({ rowCount }) =>
  Array.from({ length: rowCount }, (_, rowIndex) => {
    const lines = SKELETON_LINE_WIDTHS[rowIndex % SKELETON_LINE_WIDTHS.length];
    const translationLines =
      SKELETON_LINE_WIDTHS[(rowIndex + 2) % SKELETON_LINE_WIDTHS.length];

    return (
      <tr key={rowIndex} className={dialogueClasses["AudioWorkTableRow"]}>
        <td
          className={`${dialogueClasses["AudioWorkMetaColumn"]} ${dialogueClasses["AudioWorkTableColumnCharacter"]}`}
        >
          <Grid container direction="column" gap={0.5} alignItems="center">
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="rounded" width={100} height={22} />
            <Skeleton variant="rounded" width={100} height={22} />
          </Grid>
        </td>
        <td
          className={`${dialogueClasses["AudioWorkDataColumn"]} ${dialogueClasses["AudioWorkTableColumnOriginal"]}`}
        >
          <Skeleton variant="rounded" height={28} sx={{ mt: "26px", mb: 1 }} />
          {lines.map((width, lineIndex) => (
            <Skeleton key={lineIndex} variant="text" width={width} />
          ))}
        </td>
        <td
          className={`${dialogueClasses["AudioWorkDataColumn"]} ${dialogueClasses["AudioWorkTableColumnTranslation"]}`}
        >
          <Skeleton variant="rounded" height={28} sx={{ mt: "26px", mb: 1 }} />
          {translationLines.map((width, lineIndex) => (
            <Skeleton key={lineIndex} variant="text" width={width} />
          ))}
        </td>
        <td
          className={`${dialogueClasses["AudioWorkDataColumn"]} ${dialogueClasses["AudioWorkTableColumnNotes"]}`}
        >
          <Skeleton variant="rounded" height={72} sx={{ mt: "54px" }} />
        </td>
      </tr>
    );
  });

const QAScreen = () => {
  const params = useParams();
  const { language: languageCode } = params;
  const { state } = useContext(Store);
  const { projectFile, userInfo } = state;
  const { playbackUrl: originalVideoPlaybackUrl } = useProjectMediaPlayback({
    reference: projectFile?.videoUrl,
    projectId: projectFile?.id,
  });
  const [dubbedVideoReference, setDubbedVideoReference] = useState("");
  const [dubbedVideosList, setDubbedVideosList] = useState([]);
  // Download-only versions stay selectable for their notes and downloads, but
  // the player gets no source; load-on-play ones get it once play is pressed.
  const selectedDubbedVideo = dubbedVideoReference
    ? dubbedVideosList.find((video) => video.url === dubbedVideoReference)
    : null;
  const playback = selectedDubbedVideo
    ? versionPlayback(selectedDubbedVideo)
    : PLAYBACK.NOW;
  const downloadOnly = playback === PLAYBACK.DOWNLOAD_ONLY;
  const [loadRequestedFor, setLoadRequestedFor] = useState("");
  const waitingForPlay =
    playback === PLAYBACK.ON_PLAY && loadRequestedFor !== dubbedVideoReference;
  const { playbackUrl: dubbedVideoPlaybackUrl } = useProjectMediaPlayback({
    reference: downloadOnly ? "" : dubbedVideoReference,
    projectId: projectFile?.id,
  });
  const audioRefs = useRef([]);
  const [characters, setCharacters] = useState([]);
  const [versionQA, setVersionQA] = useState();
  const [audioData, setAudioData] = useState([]);
  useDialoguePlaybackRefresh({
    dialogues: audioData,
    projectId: projectFile?.id,
    setDialogues: setAudioData,
  });
  const [loader, setLoader] = useState(false);
  const [audioLoader] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [seekTime, setSeekTime] = useState(0);
  const [seekTimeIndex, setSeekTimeIndex] = useState(0);
  const [scrollableItem, setScrollableItem] = useState(0);
    const [scrollableItemLastUpdated, setScrollableItemLastUpdated] = useState(0);
    // const [audioLoader] = useState(false);
    // null until the QA endpoint returns the list (older backends omit it).
    const [dubbedAudiosList, setDubbedAudiosList] = useState(null);
    const [videoUniqueId, setVideoUniqueId] = useState("");
    const [videoVersion, setVideoVersion] = useState("");
    const videoSource = downloadOnly || waitingForPlay
        ? ""
        : dubbedVideoReference
        ? dubbedVideoPlaybackUrl
        : originalVideoPlaybackUrl;

    // Reset to the original video when the project changes.
    useEffect(() => {
        setDubbedVideoReference("");
        setVideoVersion("");
        setVideoUniqueId("");
    }, [languageCode, projectFile?.id]);

    const containerRef = useRef(null);
    const timeoutIdRef = useRef(null);
    const handlePausePlay = useCallback(() => {
        // Placeholder handler for future pause/play coordination
    }, []);

  const numberOfDialogues = projectFile?.numberOfDialogues || 0;
  // pagination items
  const [pageButtonActivity, setPageButtonActivity] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [goToPageNumber, setGoToPageNumber] = useState("");
  const itemsPerPage = 10;

  const [dialogueSearchFilters, setDialogueSearchFilters] = useState(
    DEFAULT_DIALOGUE_SEARCH_FILTERS
  );
  const [isDialogueSearchActive, setIsDialogueSearchActive] = useState(false);
  const [searchResultCount, setSearchResultCount] = useState(0);

  const activeDialogueCount = isDialogueSearchActive
    ? searchResultCount
    : numberOfDialogues;
  const totalPages = Math.max(1, Math.ceil(activeDialogueCount / itemsPerPage));

  const [srtDownloadLoading, setSrtDownloadLoading] = useState(false);
  const [srtDownloadError, setSrtDownloadError] = useState(null);
  const [attributionDownLoading, setAttributionDownloading] = useState(false);

  //-------------------------------------

  const [subtitles, setSubtitles] = useState([]);
  const [isLoadingSubtitles, setIsLoadingSubtitles] = useState(false);
  const [subtitleError, setSubtitleError] = useState(null);
  const [showOriginalSubtitles, setShowOriginalSubtitles] = useState(true);
  const [showTranslatedSubtitles, setShowTranslatedSubtitles] = useState(true);

  useEffect(() => {
    scrollToItem(scrollableItem - 1);
  }, [scrollableItem]);

  const checkTimeInRange = useCallback(
    (timeValue) => {
      if (audioData) {
        const itemInTimeRange = audioData.find((item) => {
          return timeValue >= item.start && timeValue < item.end;
        });

        if (itemInTimeRange) {
          if (itemInTimeRange.id !== scrollableItemLastUpdated) {
            setScrollableItemLastUpdated(itemInTimeRange.id);
            setScrollableItem(itemInTimeRange.id);
          }
        }
      }
    },
    [audioData, scrollableItemLastUpdated]
  );

  useEffect(() => {
    checkTimeInRange(currentTime);
  }, [checkTimeInRange, currentTime]);

  const scrollToItem = (index) => {
    const manpIndex = index;

    if (containerRef.current) {
      const item = containerRef.current.children[manpIndex];
      if (item) {
        item.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const displayAudioTime = useCallback((data) => {
    setCurrentTime(data);
  }, []);

  //+ Text api for dialogue
  function handleTextChange(e, index) {
    const updatedData = [...versionQA];
    updatedData[index] = {
      ...updatedData[index],
      description: e.target.value,
    };

    setVersionQA(updatedData);
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_QUALITYASSURANCE_NOTE, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 1650);
  }

  //+ Start time api for dialogue
  function handleStartTimeChange(e, index) {
    const updatedData = [...versionQA];
    updatedData[index] = {
      ...updatedData[index],
      start: timeStringToSeconds(e.target.value),
    };

    setVersionQA(updatedData);
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_QUALITYASSURANCE_NOTE, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 2300);
  }
  //+ End time api for dialogue
  function handleEndTimeChange(e, index) {
    const updatedData = [...versionQA];
    updatedData[index] = {
      ...updatedData[index],
      end: timeStringToSeconds(e.target.value),
    };

    setVersionQA(updatedData);
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_QUALITYASSURANCE_NOTE, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 2300);
  }

  //general qa
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(GET_ALL_QUALITYASSURANCE_VERSIONS, {
          projectId: projectFile.id,
          languageCode: languageCode,
        });

        const languageFilteration =
            response.data.dubbedVideosList?.filter(
                (item) => item.language === languageCode
            ) || [];

        // console.log(languageFilteration);
        // setVersionQA(sortedArray);
        setDubbedVideosList(languageFilteration);
        setDubbedAudiosList(response.data.dubbedAudiosList ?? null);
      } catch (err) {
        console.log(err);
      }
    };
    fetchData();
  }, [languageCode, projectFile?.id]);

  function fetchQaulityAssuranceVersion(uniqueId) {
    const fetchData = async () => {
      try {
        const response = await axios.post(GET_ALL_QUALITYASSURANCE_NOTES, {
          projectId: projectFile.id,
          uniqueId: uniqueId,
        });

        const sortedArray = await response.data.projectsArray.sort((a, b) => {
          if (a.start < b.start) return -1;
          if (a.start > b.start) return 1;
          if (a.end < b.end) return -1;
          if (a.end > b.end) return 1;

          return 0;
        });

        sortedArray.forEach((dia, index) => {
          dia.add = false;
          dia.id = index;
        });

        // console.log(sortedArray);

        setVersionQA(sortedArray);
      } catch (err) {
        console.log(err);
      }
    };
    fetchData();
  }

  const findTransObjectsbyLangCode = useCallback(
    (array = []) => {
      return findTranslationByLanguageCode(array, languageCode);
    },
    [languageCode]
  );

  const findDefaultEmotion = useCallback(
    (speaker, originalAudioEmotion) => {
      return getDefaultVoiceIdForEmotion(
        projectFile?.characters,
        speaker,
        originalAudioEmotion
      );
    },
    [projectFile?.characters]
  );

  function handleNoteChange(e, index) {
    const updatedData = [...audioData];

    updatedData[index].translations = updatedData[index].translations.map(
      (prev) => {
        if (prev.languageCode === languageCode) {
          return {
            ...prev,
            translatedAudioNote: e.target.value,
          };
        } else {
          return prev;
        }
      }
    );
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 1650);

    setAudioData(updatedData);
  }
  function findDisplayName(speaker) {
    return getSpeakerDisplayName(characters, speaker);
  }

  const originalAudioEmotionOptions = useMemo(
    () =>
      [
        ...new Set([
          "neutral",
          ...audioData.map((dialogue) => dialogue?.originalAudioEmotion),
          ...characters
            .flatMap((character) => character?.voices || [])
            .map((voice) => voice?.emotion),
        ]),
      ].filter(Boolean),
    [audioData, characters]
  );
  // True when this line's translated audio runs into the next line.
  const isVoiceOverlapping = (index, translatedDuration) => {
    const endTime = audioData[index].start + translatedDuration;
    const nextStart = audioData[index + 1]?.start;

    return endTime >= nextStart;
  };

  // Mirrors the old version list: "" selects the original footage. Versions are
  // matched by uniqueId because two versions can share a name.
  const handleVersionSelect = (uniqueId) => {
    if (!uniqueId) {
      setDubbedVideoReference("");
      setVideoVersion("");
      setVideoUniqueId("");
      return;
    }

    const video = dubbedVideosList.find((item) => item.uniqueId === uniqueId);

    if (!video) {
      return;
    }

    setDubbedVideoReference(video.url);
    fetchQaulityAssuranceVersion(video.uniqueId);
    setVideoVersion(video.version);
    setVideoUniqueId(video.uniqueId);
  };

  const handleTranslatedAudioVolume = async (e, index) => {
    const dialogueCopy = audioData[index];

    let volume;
    const data = e.target.value;

    if (data === null || data === "") {
      volume = null;
    } else if (isNaN(data)) {
      volume = null;
    } else {
      volume = Number(data);
    }

    dialogueCopy.translations = dialogueCopy.translations.map((prev) => {
      if (prev.languageCode === languageCode) {
        return {
          ...prev,
          translatedAudioVolume: volume,
        };
      } else {
        return prev;
      }
    });

    try {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: dialogueCopy,
      });

      // Use functional state update to safely modify the state
      setAudioData((prevData) => {
        const updatedData = [...prevData];
        updatedData[index] = dialogueCopy;
        return updatedData;
      });

      toast.info(" Audio has been updated", {
        hideProgressBar: false,
        autoClose: 1000,
      });
      // setAudioLoader(false);
    } catch (error) {
      console.error(error);
      setAudioData((prevData) => {
        const updatedData = [...prevData];
        return updatedData;
      });
    }
  };

  function handleTranslationChange(e, index) {
    const updatedData = [...audioData];
    // updatedData[index].translations = {
    //   ...updatedData[index].translations,
    //   translatedText: e.target.value,
    // };

    updatedData[index].translations = updatedData[index].translations.map(
      (prev) => {
        if (prev.languageCode === languageCode) {
          return {
            ...prev,
            translatedText: e.target.value,
          };
        } else {
          return prev;
        }
      }
    );

    setAudioData(updatedData);
    clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      await axios.post(UPDATE_ONE_SPEECH, {
        projectId: projectFile.id,
        dialogue: updatedData[index],
      });
    }, 1650);
  }

  const nextPrevPageAPI = async (action) => {
    setPageButtonActivity(true);
    if (pageNumber === 1 && action === "previous") {
      return;
    }

    if (isDialogueSearchActive) {
      const targetPageNumber =
        action === "next" ? Number(pageNumber) + 1 : Number(pageNumber) - 1;
      await loadSearchDialoguesPage(dialogueSearchFilters, targetPageNumber);
      return;
    }

    try {
      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: action,
        pageName: "voiceqa",
      });

      if (response.data) {
        const dialogues = await hydrateDialoguePlaybackUrls(
          response.data.projectsArray,
          projectFile.id
        );

        prepareDialogues(dialogues);

        setAudioData(dialogues);
        if (action === "next") {
          setPageNumber(Number(pageNumber) + 1);
        } else if (action === "previous") {
          setPageNumber(Number(pageNumber) - 1);
        }
        setPageButtonActivity(false);
      }
    } catch (err) {
      console.log(err);
      toast.error("Error in getting the next page");
    }
  };

  const goToPageAPI = async (number) => {
    if (isDialogueSearchActive) {
      const targetPageNumber = Math.min(
        totalPages,
        Math.max(1, Number(number) || 1)
      );
      await loadSearchDialoguesPage(dialogueSearchFilters, targetPageNumber);
      return;
    }

    setLoader(true);

    try {
      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: "goto",
        pageNumber: number,
        pageName: "voiceqa",
      });

      if (response.data) {
        const dialogues = await hydrateDialoguePlaybackUrls(
          response.data.projectsArray,
          projectFile.id
        );

        prepareDialogues(dialogues);

        setAudioData(dialogues);
        setGoToPageNumber("");
        setPageNumber(number);
        setLoader(false);
      }
    } catch (err) {
      setLoader(false);
      console.log(err);
      toast.error("Error in getting the next page");
    }
  };
  const checkLanguage = useCallback(
    (translations = []) => {
      return ensureProjectFinalLanguageTranslations(
        translations,
        projectFile?.finalLang
      );
    },
    [projectFile?.finalLang]
  );

  const prepareDialogues = useCallback(
    (dialogues = []) => {
      dialogues.forEach((dia, index) => {
        dia.id = index;
        dia.tempTransEmotion = findDefaultEmotion(
          dia.speaker,
          dia.originalAudioEmotion
        );
        dia.translatedAudioDurationTemp = null;
        dia.record = false;
        dia.translations = checkLanguage(dia.translations);
        dia.filterShow = true;
        dia.loader = false;
        const translation = findTransObjectsbyLangCode(dia.translations);
        dia.tempAudioList = translation?.translatedAudio
          ? [
              {
                url: translation.translatedAudio,
                length: translation.translatedAudioDuration,
                emotion: "original",
              },
            ]
          : [];
      });

      return dialogues;
    },
    [checkLanguage, findDefaultEmotion, findTransObjectsbyLangCode]
  );

  const loadSearchDialoguesPage = async (filters, targetPageNumber = 1) => {
    setLoader(true);
    setPageButtonActivity(true);

    try {
      // The backend evaluates filters on live dialogue data for this page's
      // translation language.
      const response = await axios.post(SEARCH_SPEECH_FOR_PROJECT, {
        userId: userInfo.id,
        projectId: projectFile.id,
        pageName: "voiceqa",
        pageNumber: Number(targetPageNumber),
        itemsPerPage,
        languageCode,
        speaker: filters.speaker || "",
        originalAudioEmotion: filters.originalAudioEmotion || "",
        hasTranslatedAudio: filters.hasTranslatedAudio || "",
        hasNote: filters.hasNote || "",
        attention: filters.attention || "",
        translationLanguageCode: languageCode,
      });

      const dialogues = await hydrateDialoguePlaybackUrls(
        response.data?.projectsArray ?? [],
        projectFile.id
      );

      setAudioData(prepareDialogues(dialogues));
      setSearchResultCount(
        Number(response.data?.totalCount ?? dialogues.length)
      );
      setIsDialogueSearchActive(hasAnyDialogueSearchFilter(filters));
      setPageNumber(Number(targetPageNumber));
      setGoToPageNumber("");
    } catch (err) {
      console.error(err);
      toast.error("Error in searching dialogues");
    } finally {
      setLoader(false);
      setPageButtonActivity(false);
    }
  };

  const handleDialogueSearch = async (filters) => {
    const normalizedFilters = { ...DEFAULT_DIALOGUE_SEARCH_FILTERS, ...filters };

    setDialogueSearchFilters(normalizedFilters);

    if (!hasAnyDialogueSearchFilter(normalizedFilters)) {
      setIsDialogueSearchActive(false);
      setSearchResultCount(0);
      await loadFirstDialoguePage();
      return;
    }

    await loadSearchDialoguesPage(normalizedFilters, 1);
  };

  const handleClearDialogueSearch = async () => {
    setDialogueSearchFilters(DEFAULT_DIALOGUE_SEARCH_FILTERS);
    setIsDialogueSearchActive(false);
    setSearchResultCount(0);
    await loadFirstDialoguePage();
  };

  const mediaDownloadItems = useMemo(
    () =>
      buildMediaDownloadItems({
        version: selectedDubbedVideo,
        dubbedAudiosList: dubbedAudiosList ?? projectFile?.dubbedAudiosList,
        videoUrl: projectFile?.videoUrl,
        uploadedAudioTypes: projectFile?.uploadedAudioTypes,
      }),
    [
      selectedDubbedVideo,
      dubbedAudiosList,
      projectFile?.dubbedAudiosList,
      projectFile?.videoUrl,
      projectFile?.uploadedAudioTypes,
    ]
  );

  const handleDownloadAttribution = async () => {
    if (!state.projectFile.characters) {
      toast.error('There are no characters in the project');
      return;
    }

    setAttributionDownloading(true);
    try {
      const languageNames = new Intl.DisplayNames(['en'], {
        type: 'language'
      });

      let text = `Localization Cast\n\n${languageNames.of(languageCode).toUpperCase()} (AI ASSISTED)\n\n`;
      for (const character of state.projectFile.characters) {
        text += `${character.displayName.padEnd(20, ' ')}`
        if (character.actorName) {
          text +=
            `${character.actorName.toUpperCase()} (SELF)\n` +
            `                    VARIOUS (${companyName.toUpperCase()})`;
        }

        text += '\n\n';
      }

      triggerDownload([text], 'attribution-list.txt')
    } finally {
      setAttributionDownloading(false);
    }
  }

  const triggerDownload = async (blobParts, fileName) => {
    const file = new File(blobParts, fileName, {
      type: 'text/plain',
    });

    const link = document.createElement('a');
    const url = URL.createObjectURL(file);
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  const handleDownloadSRT = async () => {
    try {
      setSrtDownloadLoading(true);
      setSrtDownloadError(null);

      // Get the URL from your API
      const response = await axios.post(SRT, {
        projectId: projectFile.id,
        language: languageCode
      });

      // Fetch the file content from the URL
      const fileResponse = await axios.get(response.data.url, {
        responseType: 'blob' // Important: tells axios to handle response as binary data
      });

      // Create a blob URL from the file content
      const blob = new Blob([fileResponse.data], { type: 'text/plain' });
      const blobUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = response.data.filename;//`srt-${languageCode}.srt`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl); // Free up memory

      toast.success('SRT file downloaded successfully');
    } catch (err) {
      setSrtDownloadError(err.message);
      toast.error(`Error downloading SRT file ${err.response?.data?.error || ''}`);
      console.error('Error downloading SRT:', err);
    } finally {
      setSrtDownloadLoading(false);
    }
  };


  //apis - get characters
  useEffect(() => {
    if (!projectFile?.id) {
      return;
    }
    const fetchData = async () => {
      try {
        const response = await axios.post(GET_CHARACTERS_FOR_PROJECT, {
          projectId: projectFile.id,
        });

        setCharacters(response.data.characters);
      } catch (err) {
        console.log(err);
      }
    };
    fetchData();
  }, [projectFile.id]);

  //apis - dialogue
  const loadFirstDialoguePage = useCallback(async () => {
    if (!projectFile?.id || !userInfo?.id) {
      return;
    }

    setLoader(true);
    try {
      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: "first",
        Current: null,
        pageName: "voiceqa",
      });

      const dialogues = await hydrateDialoguePlaybackUrls(
        response.data.projectsArray,
        projectFile.id
      );

      setAudioData(prepareDialogues(dialogues));
      setPageNumber(1);
      setGoToPageNumber("");
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  }, [prepareDialogues, projectFile?.id, userInfo?.id]);

  useEffect(() => {
    loadFirstDialoguePage();
  }, [loadFirstDialoguePage]);

  const handleTimeSync = async (time) => {
    try {
      setLoader(true);
      const positionResponse = await axios.post(GET_SPEECH_POSITION_AROUND_TIME, {
        projectId: projectFile.id,
        time: time
      });

      const number = Math.ceil(positionResponse.data.firstPosition / 10);

      setDialogueSearchFilters(DEFAULT_DIALOGUE_SEARCH_FILTERS);
      setIsDialogueSearchActive(false);
      setSearchResultCount(0);

      const response = await axios.post(GET_SPEECH_FOR_PROJECT_BY_PAGE, {
        userId: userInfo.id,
        projectId: projectFile.id,
        action: "goto",
        pageNumber: number,
        pageName: "voiceqa",
      });

      if (response.data) {
        const dialogues = await hydrateDialoguePlaybackUrls(
          response.data.projectsArray,
          projectFile.id
        );

        prepareDialogues(dialogues);

        setAudioData(dialogues);
        setGoToPageNumber("");
        setPageNumber(number);
        setLoader(false);
      }
    } catch (err) {
      setLoader(false);
      console.log(err);
      toast.error("Error syncing to current time position");
    }
  };

  useEffect(() => {
    const fetchSubtitles = async () => {
      try {
        setIsLoadingSubtitles(true);
        setSubtitleError(null);

        const response = await axios.post(GET_SRT, {
          projectId: projectFile.id,
          language: languageCode,
          startTime: 0,
          endTime: 100000 // Use a large value to get all subtitles
        });

        if (response.data && response.data.subtitles) {
          setSubtitles(response.data.subtitles);
        } else {
          setSubtitles([]);
          console.warn("Subtitle data format not as expected");
        }
      } catch (err) {
        console.error("Error fetching subtitles:", err);
        setSubtitleError(err.message);
        toast.error("Failed to load subtitles");
      } finally {
        setIsLoadingSubtitles(false);
      }
    };

    if (projectFile.id && languageCode) {
      fetchSubtitles();
    }
  }, [projectFile.id, languageCode]);

  const toggleOriginalSubtitles = () => {
    setShowOriginalSubtitles(!showOriginalSubtitles);
  };

  const toggleTranslatedSubtitles = () => {
    setShowTranslatedSubtitles(!showTranslatedSubtitles);
  };

  const getLanguageDisplayName = (languageCode) => {
    try {
      // Use the Intl API to get the display name of languages in the user's locale
      const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
      return displayNames.of(languageCode);
    } catch (error) {
      // Fallback in case of issues
      console.warn("Could not get language display name:", error);
      return languageCode.toUpperCase();
    }
  };

  if (!projectFile?.id) {
    return <Loader />;
  }

  const originalLanguageDisplay = getLanguageDisplayName(
    projectFile.originalLanguage || projectFile.orginalLang || "en"
  );
  const translatedLanguageDisplay = getLanguageDisplayName(languageCode);

  return (
    <Grid
      item
      container
      justifyContent="center"
      marginTop={0.5}
      style={{ fontFamily: "var(--font-body)" }}
    >
      <Loader Load={audioLoader} />
      <ToastContainer position="top-center" />
      <Grid item container xs={12} direction="column" sx={{ px: 1 }}>
        <Grid item container direction="row">
          {/* Versions list beside the video; the pair stays pinned while scrolling. */}
          <Grid
            item
            xs={12}
            container
            direction="row"
            wrap="nowrap"
            gap={1}
            className={classes["QAVideoRow"]}
            // Same fixed background as the page, so dialogues scrolling under
            // the pinned row don't show through its gaps.
            sx={{ ...backgroundWellSx, pb: 1 }}
          >
            <nav
              className={classes["QAVersionList"]}
              aria-label="Video versions"
            >
              <div className={classes["QAVersionListTitle"]}>Versions</div>
              <div className={classes["QAVersionListItems"]}>
                {[
                  { uniqueId: "", version: "Original", date: "Original footage" },
                  ...dubbedVideosList.map((video) => ({
                    uniqueId: video.uniqueId,
                    version: video.version,
                    date: `${convertToDate(video.creationDate)} · ${audioLayoutLabel(
                      video
                    )} · ${videoSourceLabel(video)}${
                      isPlayableVersion(video) ? "" : " · Download only"
                    }`,
                  })),
                ].map((item) => {
                  const isSelected =
                    item.uniqueId === ""
                      ? videoVersion === ""
                      : videoVersion !== "" && videoUniqueId === item.uniqueId;

                  return (
                    <button
                      key={item.uniqueId || "original"}
                      type="button"
                      aria-pressed={isSelected}
                      className={`${classes["QAVersionListItem"]} ${
                        isSelected ? classes["QAVersionListItemSelected"] : ""
                      }`}
                      onClick={() => handleVersionSelect(item.uniqueId)}
                    >
                      <span className={classes["QAVersionListName"]}>
                        {item.version}
                      </span>
                      <span className={classes["QAVersionListDate"]}>
                        {item.date}
                      </span>
                    </button>
                  );
                })}
              </div>
            </nav>

            <Grid item xs sx={{ minWidth: 0, position: "relative" }}>
              {downloadOnly && <DownloadOnlyNotice />}
              <UnifiedVideoPlayer
                source={videoSource}
                onPlayWithoutSource={
                  waitingForPlay
                    ? () => setLoadRequestedFor(dubbedVideoReference)
                    : undefined
                }
                seekTime={seekTime}
                displayAudioTime={displayAudioTime}
                seekTimeIndex={seekTimeIndex}
                setPausePlay={handlePausePlay}
                layout="horizontal"
                dualAudio={false}
                showDebug={false}
                compactControls
                customClasses={{
                  container: classes["AudioOutPutHorizontal"],
                  // The surrounding row is what stays pinned, not the player.
                  containerStyles: { position: "relative", top: "auto" },
                  videoContainer: classes["AudioOutPutVideoHorizontal"],
                  controlsContainer: classes["AudioOutPutController"],
                  timeDisplay: classes["AudioOutPutShownTime"],
                  selector: classes["AudioOutPutShowTimeOptionBox"],
                }}
                onTimeSync={handleTimeSync}
                syncOnSliderSeek
                subtitles={subtitles}
                showOriginalSubtitles={showOriginalSubtitles}
                showSubtitles={showTranslatedSubtitles}
                toggleOriginalSubtitles={toggleOriginalSubtitles}
                toggleTranslatedSubtitles={toggleTranslatedSubtitles}
                originalLanguageDisplay={originalLanguageDisplay}
                translatedLanguageDisplay={translatedLanguageDisplay}
              />
            </Grid>

            <section
              className={classes["QASidePanel"]}
              aria-label="Downloads"
            >
              <div className={classes["QASidePanelTitle"]}>Downloads</div>
              <div className={classes["QASidePanelItems"]}>
                <MediaDownloadMenu
                  projectId={projectFile?.id}
                  items={mediaDownloadItems}
                  renderTrigger={(triggerProps) => (
                    <button
                      type="button"
                      className={classes["QAActionItem"]}
                      {...triggerProps}
                    >
                      <MovieOutlinedIcon fontSize="small" />
                      <span className={classes["QAActionText"]}>
                        <span className={classes["QAActionTitle"]}>
                          Media files
                        </span>
                        <span className={classes["QAActionDetail"]}>
                          {selectedDubbedVideo
                            ? selectedDubbedVideo.version
                            : "Original"}
                        </span>
                      </span>
                      <ArrowDropDownIcon fontSize="small" />
                    </button>
                  )}
                />

                <button
                  type="button"
                  className={classes["QAActionItem"]}
                  onClick={handleDownloadAttribution}
                  disabled={attributionDownLoading}
                >
                  {attributionDownLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DescriptionOutlinedIcon fontSize="small" />
                  )}
                  <span className={classes["QAActionText"]}>
                    <span className={classes["QAActionTitle"]}>
                      Attribution list
                    </span>
                    <span className={classes["QAActionDetail"]}>
                      {attributionDownLoading ? "Generating…" : "Credits for this project"}
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  className={classes["QAActionItem"]}
                  onClick={handleDownloadSRT}
                  disabled={srtDownloadLoading}
                >
                  {srtDownloadLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SubtitlesOutlinedIcon fontSize="small" />
                  )}
                  <span className={classes["QAActionText"]}>
                    <span className={classes["QAActionTitle"]}>
                      Subtitles (SRT)
                    </span>
                    <span className={classes["QAActionDetail"]}>
                      {srtDownloadLoading ? "Generating…" : "Translated subtitle file"}
                    </span>
                  </span>
                </button>
              </div>

              {(srtDownloadError || isLoadingSubtitles || subtitleError) && (
                <div className={classes["QASidePanelStatus"]}>
                  {srtDownloadError && (
                    <span className={classes["QASidePanelError"]}>
                      Error downloading SRT
                    </span>
                  )}
                  {isLoadingSubtitles && <span>Loading subtitles…</span>}
                  {subtitleError && (
                    <span className={classes["QASidePanelError"]}>
                      Subtitles could not be loaded: {subtitleError}
                    </span>
                  )}
                </div>
              )}
            </section>
          </Grid>

          <Grid
            item
            xs={12}
            container
            alignItems="center"
            justifyContent="flex-end"
            sx={{ mt: 0.5, mb: 1 }}
          >
            <Grid item>
              <DialogueFilterBar
                compact
                value={dialogueSearchFilters}
                onChange={handleDialogueSearch}
                onClear={handleClearDialogueSearch}
                characters={characters}
                getSpeakerDisplayName={findDisplayName}
                emotionOptions={originalAudioEmotionOptions}
                attentionOptions={DIALOGUE_ATTENTION_OPTIONS}
                showNoteFilter
                resultCount={isDialogueSearchActive ? searchResultCount : null}
                totalCount={numberOfDialogues}
                disabled={loader || pageButtonActivity}
              />
            </Grid>
          </Grid>

          <Grid item container xs={12}>
            <div className={dialogueClasses["AudioWorkTableWrapper"]}>
              <table className={dialogueClasses["AudioWorkTable"]}>
                <thead>
                  <tr className={dialogueClasses["AudioWorkTableHeadRow"]}>
                    <th
                      className={`${dialogueClasses["AudioWorkTableHeaderSpacer"]} ${dialogueClasses["AudioWorkTableColumnCharacter"]}`}
                    >
                      Character
                    </th>
                    {/* Equal shares, so the tag and player row don't widen Translation. */}
                    <th
                      className={`${dialogueClasses["AudioWorkTableHeaderCell"]} ${dialogueClasses["AudioWorkTableColumnOriginal"]}`}
                      style={{ width: "35%" }}
                    >
                      Original
                    </th>
                    <th
                      className={`${dialogueClasses["AudioWorkTableHeaderCell"]} ${dialogueClasses["AudioWorkTableColumnTranslation"]}`}
                      style={{ width: "35%" }}
                    >
                      Translation
                    </th>
                    <th
                      className={`${dialogueClasses["AudioWorkTableHeaderCell"]} ${dialogueClasses["AudioWorkTableColumnNotes"]}`}
                    >
                      Notes
                    </th>
                  </tr>
                </thead>

                <tbody ref={containerRef}>
                  {dubbedVideosList.length === 0 ? (
                    <tr className={dialogueClasses["AudioWorkTableRow"]}>
                      <td
                        colSpan={4}
                        className={dialogueClasses["AudioWorkDataColumn"]}
                        style={{ textAlign: "center" }}
                      >
                        <Typography>No Versions Ready</Typography>
                      </td>
                    </tr>
                  ) : loader ? (
                    <DialogueTableSkeletonRows rowCount={itemsPerPage} />
                  ) : (
                    audioData?.map((data, index) => {
                      const translation = findTransObjectsbyLangCode(
                        data?.translations
                      );
                      const translationDuration =
                        translation?.translatedAudioDuration ||
                        data?.translatedAudioDurationTemp;
                      const durationNote = getTranslationDurationMessage(
                        data.end - data.start,
                        translationDuration
                      );
                      const isOverlapping = isVoiceOverlapping(
                        data.id,
                        translation?.translatedAudio
                          ? translation?.translatedAudioDuration
                          : null
                      );
                      const isActiveLine =
                        currentTime >= data.start && currentTime < data.end;
                      const activeTextClass = isActiveLine
                        ? dialogueClasses["DialogueRowActiveText"]
                        : "";
                      const seekToLine = () => {
                        setSeekTime(data.start);
                        setSeekTimeIndex((prevIndex) => prevIndex + 1);
                      };

                      return (
                        <tr
                          key={data.dbid || index}
                          className={dialogueClasses["AudioWorkTableRow"]}
                        >
                          <td
                            className={`${dialogueClasses["AudioWorkMetaColumn"]} ${dialogueClasses["AudioWorkTableColumnCharacter"]}`}
                          >
                            <Grid
                              container
                              direction="column"
                              gap={0.5}
                              alignItems="center"
                            >
                              <Grid item>{findDisplayName(data.speaker)}</Grid>
                              <Grid item>
                                <textarea
                                  className={
                                    classes["AudioWorkPlaceTimelineEdit"]
                                  }
                                  value={formatTime(data.start)}
                                  type="text"
                                  onChange={(e) =>
                                    handleStartTimeChange(e, data.id)
                                  }
                                ></textarea>
                              </Grid>
                              <Grid item>
                                <textarea
                                  className={
                                    classes["AudioWorkPlaceTimelineEdit"]
                                  }
                                  value={formatTime(data.end)}
                                  type="text"
                                  onChange={(e) =>
                                    handleEndTimeChange(e, data.id)
                                  }
                                ></textarea>
                              </Grid>
                            </Grid>
                          </td>

                          <td
                            className={`${dialogueClasses["AudioWorkDataColumn"]} ${dialogueClasses["AudioWorkTableColumnOriginal"]}`}
                            onClick={seekToLine}
                            style={{ cursor: "pointer" }}
                          >
                            <Grid container direction="column">
                              <Grid item>
                                {/* Keeps the text box level with the translation's status tags. */}
                                <Grid
                                  item
                                  className={
                                    dialogueClasses["DialogueRowHeaderLine"]
                                  }
                                />

                                <div
                                  className={
                                    dialogueClasses["DialogueRowAudioSection"]
                                  }
                                  style={{ width: "98%" }}
                                >
                                  <NativeClipAudioPlayer
                                    src={data.originalAudio}
                                    timeDecimalPlaces={3}
                                  />
                                </div>
                              </Grid>

                              <Grid item>
                                <textarea
                                  className={`${dialogueClasses["AudioWorkTextEditInput"]} ${dialogueClasses["DialogueRowJoinedTextArea"]} ${activeTextClass}`}
                                  value={data.text}
                                  type="text"
                                  onChange={(e) => handleTextChange(e, data.id)}
                                ></textarea>
                              </Grid>
                            </Grid>
                          </td>

                          <td
                            className={`${dialogueClasses["AudioWorkDataColumn"]} ${dialogueClasses["AudioWorkTableColumnTranslation"]}`}
                            onClick={seekToLine}
                            style={{ cursor: "pointer" }}
                          >
                            {data.keepOriginalText ? null : (
                              <Grid container direction="column">
                                <Grid
                                  item
                                  className={`${dialogueClasses["DialogueRowHeaderLine"]} ${dialogueClasses["DialogueRowStatusTags"]}`}
                                >
                                  {durationNote ? (
                                    <span
                                      className={`${
                                        dialogueClasses["DialogueRowStatusTag"]
                                      } ${
                                        durationNote.label === "Perfect"
                                          ? dialogueClasses[
                                              "DialogueRowStatusTagSuccess"
                                            ]
                                          : ""
                                      }`}
                                    >
                                      {durationNote.label}
                                    </span>
                                  ) : null}
                                  {isOverlapping ? (
                                    <span
                                      className={
                                        dialogueClasses["DialogueRowStatusTag"]
                                      }
                                    >
                                      Overlapping
                                    </span>
                                  ) : null}
                                </Grid>

                                <Grid
                                  item
                                  container
                                  direction="row"
                                  alignItems="center"
                                  width="98%"
                                  className={`${dialogueClasses["DialogueRowTranslatedPlayerRow"]} ${dialogueClasses["DialogueRowAudioSection"]}`}
                                >
                                  <Grid item height="100%">
                                    <VolumeSelector
                                      className={
                                        dialogueClasses[
                                          "DialogueRowCompactVolume"
                                        ]
                                      }
                                      value={
                                        translation?.translatedAudioVolume ??
                                        null
                                      }
                                      onChange={(e) => {
                                        handleTranslatedAudioVolume(
                                          e,
                                          data.id
                                        );
                                      }}
                                    />
                                  </Grid>

                                  <Grid item xs height="100%">
                                    <ClipAudioPlayer
                                      compact
                                      embedded
                                      showScrubBar={false}
                                      timeDecimalPlaces={3}
                                      ref={(el) =>
                                        (audioRefs.current[index] = el)
                                      }
                                      src={translation?.translatedAudio}
                                      decible={
                                        translation?.translatedAudioVolume ??
                                        null
                                      }
                                    />
                                  </Grid>
                                </Grid>

                                <Grid item>
                                  <textarea
                                    className={`${dialogueClasses["AudioWorkTextEditInput"]} ${dialogueClasses["DialogueRowJoinedTextArea"]} ${activeTextClass}`}
                                    value={translation?.translatedText ?? ""}
                                    type="text"
                                    onChange={(e) =>
                                      handleTranslationChange(e, data.id)
                                    }
                                  ></textarea>
                                </Grid>
                              </Grid>
                            )}
                          </td>

                          <td
                            className={`${dialogueClasses["AudioWorkDataColumn"]} ${dialogueClasses["AudioWorkTableColumnNotes"]}`}
                          >
                            <Grid container direction="column">
                              <Grid
                                item
                                width="100%"
                                className={
                                  dialogueClasses["DialogueRowTextBoxOffset"]
                                }
                              >
                                <textarea
                                  className={
                                    dialogueClasses["AudioWorkNoteEditInput"]
                                  }
                                  value={translation?.translatedAudioNote || ""}
                                  placeholder="add note here"
                                  type="text"
                                  onChange={(e) => handleNoteChange(e, data.id)}
                                ></textarea>
                              </Grid>
                            </Grid>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Grid>

          <Grid
            item
            container
            alignItems="center"
            justifyContent="center"
            gap={2}
            marginTop={2}
          >
            <Grid item>
              <TaskButton
                onClick={() => nextPrevPageAPI("previous")}
                width="80px"
                disabled={Number(pageNumber) === 1 || pageButtonActivity}
              >
                Previous
              </TaskButton>
            </Grid>

            <Grid item>
              <span>
                Page {pageNumber} of {totalPages}
              </span>
            </Grid>

            <Grid item>
              <TaskButton
                width="80px"
                onClick={() => nextPrevPageAPI("next")}
                disabled={
                  Number(pageNumber) === totalPages || pageButtonActivity
                }
              >
                Next
              </TaskButton>
            </Grid>
          </Grid>

          <Grid
            item
            container
            alignItems="center"
            justifyContent="center"
            gap={4}
            marginTop={1}
          >
            <Grid item>
              <input
                type="text"
                style={{ width: "40px" }}
                value={goToPageNumber}
                onChange={(e) => {
                  setGoToPageNumber(e.target.value >= 1 ? e.target.value : 1);
                }}
              />
            </Grid>
            <Grid item>
              <TaskButton
                onClick={() => goToPageAPI(goToPageNumber)}
                width="80px"
              >
                Go
              </TaskButton>
            </Grid>
          </Grid>

        </Grid>
      </Grid>
    </Grid>
  );
};

export default QAScreen;
